<template>
  <div class="app-container">
    <Sidebar
        :sidebarOpen="sidebarOpen"
        :conversations="Array.from(sessionHandler.sessionMap.values())"
        :activeId="sessionHandler.selectSessionId"
        @create-chat="createChat"
        @select-chat="selectChat"
        @show_product_description="show_product_description"
    />

    <main class="main-content">
      <TopBar
          :isLogin="isLogin"
          @toggleSidebar="toggleSidebar"
          @toggleLoginStatus="toggleShowLoginModal"
          @toggleUserInfo="toggleShowUseMenu"
      ></TopBar>
      <UserMenu
          :show="showUserMenu"
          :userName="userData.username"
          :regDate="userData.regDate"
          :totalSpend="userData.totalSpend"
          @switchUser="switchUser"
          @logout="logout"
      ></UserMenu>
      <div class="chat-viewport" ref="scrollRef">
        <div class="message-list">
          <div v-for="(msg, i) in sessionHandler.getActiveSession().msgList" :key="i" class="message-row">
            <ChatBubble
                :role="msg.role"
                :content="msg.content"
                :isStreaming="msg.isStreaming"
                :statusKey="msg.statusText"
                :msgType="msg.msgType"
                :chartData="msg.chartData"
                :field="msg.field"
                :title="msg.title"
                :subTitle="msg.subTitle"
            />
          </div>
        </div>
      </div>

      <footer class="input-area">
        <div class="input-wrapper">
          <!-- 单个切换按钮 -->
          <button
              class="mode-toggle"
              :class="msgState"
              @click="toggleChatModel"
              :disabled="msgStateMachine.isChatting.value"
          >
            {{ msgStateLabel }}
          </button>

          <!-- 输入框（placeholder 绑定到 computed） -->
          <textarea
              ref="textareaRef"
              v-model="userInput"
              placeholder="キーワードを入力..."
              @input="autoResize"
              @keydown="handleKeydown"
              rows="1"
              :disabled="msgStateMachine.isChatting.value"
          />

          <SpeechInput @recognized="userInput = $event"/>

          <button
              @click="handleSendOrStop"
              class="send-btn"
              :class="{ active: userInput.trim(), stop: msgStateMachine.isChatting.value }"
              :disabled="!userInput.trim() && !msgStateMachine.isChatting.value"
          >
            <Send
                v-if="!msgStateMachine.isChatting.value"
                class="send-icon"
            />

            <div
                v-else
                class="stop-square"
            ></div>
          </button>
        </div>
      </footer>
    </main>
    <LoginModal
        :show="showLoginModal"
        @close="closeLoginModal"
        @login="(user, pass) => userLogin(user, pass)"
    />
  </div>
</template>

<script setup>
import {ref, nextTick, computed, reactive} from 'vue';
import SpeechInput from '../components/SpeechInput.vue';
import {Send} from 'lucide-vue-next'
import Sidebar from "@/components/layout/Sidebar.vue";
import TopBar from "@/components/layout/TopBar.vue";
import UserMenu from "@/components/user/UserMenu.vue";
import LoginModal from "@/components/user/LoginModal.vue";
import ChatBubble from '../components/chat/ChatBubble.vue'
import {MessageStateMachine} from "@/common/State/MessageStateMachine.ts";
import {SessionHandler} from "@/common/session/sessionHandler.ts";
import {MessageFactory} from "@/common/factory/MessageFactory.ts";
import {MessageType} from "@/common/session/MessageItem.ts";

// ----------------------------------------------

// 消息状态机
const msgStateMachine = new MessageStateMachine()
msgStateMachine.switchState(MessageStateMachine.STATE_SEARCH)
// 会话管理
const sessionHandler = reactive(new SessionHandler())

// 控制左侧菜单是否显示
const sidebarOpen = ref(true)
// 登录状态
const isLogin = ref(false)
// 用户菜单显示
const showUserMenu = ref(false);
// 用户登录显示
const showLoginModal = ref(false)
// 用户数据
const userData = ref({username: 'System Admin', regDate: '2026/01/22', totalSpend: 128450});

const msgState = ref(msgStateMachine.getStateKey())

const textareaRef = ref(null)
const userInput = ref('');
const scrollRef = ref(null);
let sessionId = 1

sessionHandler.createSession(SessionHandler.DEFAULT_SESSION_KEY, "新規会話")
pushDemoSession()

const msgStateLabel = computed(() => {
  if (msgState.value === MessageStateMachine.STATE_SEARCH) return '検索'
  if (msgState.value === MessageStateMachine.STATE_AGENT) return 'Agent'
  return ''
})

const logout = () => {
  isLogin.value = false;
  showUserMenu.value = false;
};
const switchUser = () => {
  showUserMenu.value = false;
  isLogin.value = false;
  showLoginModal.value = true;
};

//登录接口
const userLogin = async (userName, password) => {

  showLoginModal.value = false;
  isLogin.value = true;

}

const closeLoginModal = () => {

  showLoginModal.value = false;

}

const toggleShowLoginModal = () => {
  if (!isLogin.value) {
    showLoginModal.value = true
  }
}

const toggleShowUseMenu = () => {
  if (isLogin.value) {
    showUserMenu.value = !showUserMenu.value;
  }
}

const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value
}

const createChat = () => {
  const newId = Date.now().toString()

  sessionHandler.createSession(newId, "新規対話" + sessionId)
  sessionHandler.selectSessionId = newId
  sessionId++
}

const selectChat = (id) => {
  sessionHandler.selectSessionId = id
}

const autoResize = () => {
  const el = textareaRef.value
  if (!el) return

  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}

const resetTextareaHeight = () => {
  const el = textareaRef.value
  if (!el) return
  el.style.height = 'auto'
}

const handleKeydown = (e) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    send()
  }
}

const show_product_description = () => {

  const msgItem = MessageFactory.createAiMsg()
  msgItem.content =
      "・本デモは、mcframe Data Copilot の機能および活用イメージをご紹介することを目的としています。\n" +
      "・本デモ環境は検証・紹介用であり、本番環境としてはご利用いただけません。\n" +
      "・実際の製品版とは、一部の機能や画面仕様が異なる場合があります。\n" +
      "・機能および仕様は、予告なく変更される可能性があります。\n" +
      "・ネットワーク状況や処理の複雑さにより、回答に時間を要する場合があります。\n" +
      "・本製品は、以下の業務領域をサポートしています。\n" +
      "　　> 検索機能： 受注、売上、出荷\n" +
      "　　> 予測機能： 受注\n" +
      "・表示されるデータはすべてテスト用の架空データです。\n" +
      "・検索を行う際は、2026年6月の期間をご利用ください。"

  sessionHandler.getActiveSession().pushMessage(msgItem)

}

const toggleChatModel = () => {

  if(msgStateMachine.getStateKey() === MessageStateMachine.STATE_AGENT) {
    msgStateMachine.switchState(MessageStateMachine.STATE_SEARCH)
  }else {
    msgStateMachine.switchState(MessageStateMachine.STATE_AGENT)
  }

  msgState.value = msgStateMachine.getStateKey()

}

const handleSendOrStop = () => {

  if (msgStateMachine.isChatting.value) {
    msgStateMachine.closeSSE()

    if (sessionHandler.getActiveSession().lastMessage.isStreaming) {

      const lastMsg = sessionHandler.getActiveSession().lastMessage

      if (lastMsg && lastMsg.isStreaming) {
        lastMsg.isStreaming = false
      }

    }

  }else {
    send()
  }

}

const send = async () => {
  const text = userInput.value.trim();
  if (!text || msgStateMachine.isChatting.value) return;

  // 用户输入上屏

  const msgItem = MessageFactory.createUserMsg()
  msgItem.content = text

  sessionHandler.getActiveSession().pushMessage(msgItem)
  userInput.value = '';
  msgStateMachine.isChatting = true;
  resetTextareaHeight()

  await nextTick();
  scrollRef.value.scrollTo({top: scrollRef.value.scrollHeight, behavior: 'smooth'});

  try {
    await msgStateMachine.handler(text, sessionHandler.getActiveSession())

  } catch (e) {
    console.error('API Error:', e);
  } finally {
    await nextTick();
    scrollRef.value.scrollTo({top: scrollRef.value.scrollHeight, behavior: 'smooth'});
  }
};

function pushDemoSession() {

  pushDemoSession01()
  pushDemoSession02()
  pushDemoSession03()
  pushDemoSession04()
  sessionHandler.selectSessionId = SessionHandler.DEFAULT_SESSION_KEY

}

function pushDemoSession01() {

  sessionHandler.createSession("demo01", "[サンプル] 今四半期の売上予定金額と受注金額を比較し、売上転換率を算出してください\n")
  let msgItem = MessageFactory.createUserMsg()
  msgItem.content = "今四半期の売上予定金額と受注金額を比較し、売上転換率を算出してください"
  sessionHandler.getActiveSession().pushMessage(msgItem)

  msgItem = MessageFactory.createAiMsg()
  msgItem.msgType = MessageType.TABLE
  msgItem.field = ["売上予定金額", "受注金額", "売上転換率"]
  msgItem.chartData =  [[215000, 430000, 50]]
  sessionHandler.getActiveSession().pushMessage(msgItem)
  msgItem = MessageFactory.createAiMsg()
  msgItem.content = "1. 主要発見:\n" +
      "- 売上予定金額 = 215,000、受注金額 = 430,000。\n" +
      "- 計算上の売上転換率 = 215,000 ÷ 430,000 = 50%。\n" +
      "\n" +
      "2. 業務リスク:\n" +
      "- 売上転換率50%は、受注に対して売上化が半分しか達成できていないことを示し、収益実現のギャップリスクがある。\n" +
      "- 受注→売上の各プロセスにボトルネック（キャンセル・納期遅延・仕様変更等）が存在する可能性。\n" +
      "- 予測・計画の精度が不足しているリスク（受注に対して売上予定が適切に算出されていない可能性）。\n" +
      "- キャッシュフロー、在庫、製造計画との不整合リスク（受注実績と売上予定の乖離による影響）。\n" +
      "\n" +
      "3. 改善提案:\n" +
      "- 未転換分の原因を分類（キャンセル、納期遅延、仕様変更、受注取り消しなど）して比率を把握し、主要原因から対策を実行する。\n" +
      "- 受注→出荷→請求の各プロセスをレビューしてリードタイムと障害点を特定し、標準化・自動化で改善する（注文確認・在庫引当を優先）。\n" +
      "- 売上予定の算出基準を明確化し、受注実績との差分を月次でレビューして予測精度を向上させる。\n" +
      "- 在庫・生産・キャッシュフロー計画と売上予定を連動させ、乖離に応じた安全在庫・生産調整ルールを設定する。\n" +
      "- KPI（転換率目標、未転換率、リードタイム等）を設定して定期的にモニタリングする。"
  sessionHandler.getActiveSession().pushMessage(msgItem)

}

function pushDemoSession02() {

  sessionHandler.createSession("demo02", "[サンプル] 受注に取引数量が最も多い品目五つください\n")
  let msgItem = MessageFactory.createUserMsg()
  msgItem.content = "受注に取引数量が最も多い品目五つください"
  sessionHandler.getActiveSession().pushMessage(msgItem)

  msgItem = MessageFactory.createAiMsg()
  msgItem.msgType = MessageType.TABLE
  msgItem.field =  ["品目ＣＤ", "品名", "取引単位ＣＤ", "取引単位数量"]
  msgItem.chartData =  [["01300103", "品名242", "case", 4030], ["03040105", "品名185", "case", 3512], ["03010105", "品名218", "case", 1205], ["02600205", "品名193", "case", 1016], ["04200105", "品名151", "case", 300]]
  sessionHandler.getActiveSession().pushMessage(msgItem)

  msgItem = MessageFactory.createAiMsg()
  msgItem.msgType = MessageType.BAR
  msgItem.title = "受注の取引数量上位5品目"
  msgItem.chartData = {"品名242": 4030, "品名185": 3512, "品名218": 1205, "品名193": 1016, "品名151": 300}
  sessionHandler.getActiveSession().pushMessage(msgItem)

  msgItem = MessageFactory.createAiMsg()
  msgItem.content = "1. 主要発見\n" +
      "- 提供データにおける取引数量（取引単位数量）上位5件（降順）は以下の通り（全て取引単位ＣＤ＝case、合計取引数量＝10,063）：\n" +
      "  1. 品目ＣＤ 01300103 / 品名242 / case / 4,030（約40.1%）\n" +
      "  2. 品目ＣＤ 03040105 / 品名185 / case / 3,512（約34.9%）\n" +
      "  3. 品目ＣＤ 03010105 / 品名218 / case / 1,205（約12.0%）\n" +
      "  4. 品目ＣＤ 02600205 / 品名193 / case / 1,016（約10.1%）\n" +
      "  5. 品目ＣＤ 04200105 / 品名151 / case / 300（約3.0%）\n" +
      "\n" +
      "2. 業務リスク\n" +
      "- リスクA（依存集中）: 上位2品目で合計約75.0%を占めており、これらに供給・在庫問題が発生すると受注対応に大きく影響する。\n" +
      "- リスクB（品切れリスク）: 取引数量の大きい品目は需要・リードタイム変動で在庫切れリスクが高まる。\n" +
      "- リスクC（優先度判断不足）: データは取引数量のみで、売上金額・利益率・在庫状況がないため、どの品目に重点を置くべきか不明確。\n" +
      "- リスクD（低出荷品目の取り扱い）: 取引数量の非常に少ない品目は在庫回転が悪化する可能性（保管コスト・陳腐化）。\n" +
      "- リスクE（データ不足による分析制約）: 発注頻度、リードタイム、在庫残高、欠品履歴などが無いため詳細な在庫最適化はできない。\n" +
      "\n" +
      "3. 改善提案\n" +
      "- リスクAへの対策:\n" +
      "  - 上位2品目については主要・代替サプライヤーを確保し、供給途絶時の代替手順を作成する。\n" +
      "  - これら品目の安全在庫を見直し（ABC分析でAランクに設定）、優先的に管理する。\n" +
      "- リスクBへの対策:\n" +
      "  - 発注点（ROP）と発注量（EOQまたは実務ルール）を設定し、リードタイム変動を加味した安全在庫を導入する。\n" +
      "  - リアルタイム在庫モニタリングとアラートを設ける。\n" +
      "- リスクCへの対策:\n" +
      "  - 取引数量に加え、売上額・粗利・在庫回転率を収集してABC/XYZ分析を実施し、品目ごとのサービスレベルを決定する。\n" +
      "- リスクDへの対策:\n" +
      "  - 低取引数量品はSKU整理（継続要否の評価）またはロット発注・受注生産の検討を行い、保管コストを削減する。\n" +
      "- リスクEへの対策:\n" +
      "  - 必要データ（在庫残高、発注履歴、リードタイム、欠品履歴、売上金額）を収集・統合し、継続的な分析基盤を構築する。\n" +
      "\n" +
      "必要なら、上記項目について優先順位付け（例：Aランク品目の安全在庫設定から着手）や具体的な目標値算出（安全在庫日数・サービスレベル）を支援します。"
  sessionHandler.getActiveSession().pushMessage(msgItem)

}

function pushDemoSession03() {

  sessionHandler.createSession("demo03", "[サンプル] 受注に各品目取引数量の割合\n")
  let msgItem = MessageFactory.createUserMsg()
  msgItem.content = "受注に各品目取引数量の割合"
  sessionHandler.getActiveSession().pushMessage(msgItem)

  msgItem = MessageFactory.createAiMsg()
  msgItem.msgType = MessageType.TABLE
  msgItem.field = ["受注ＮＯ", "品目ＣＤ", "品名", "取引単位ＣＤ", "取引単位数量", "受注内取引数量割合"]
  msgItem.chartData = [["SO2606000030", "07100105", "品名12", "case", 2, 1], ["SO2606000021", "07200205", "品名23", "case", 2, 1], ["SO2606000022", "03050105", "品名101", "case", 1, 1], ["SO2606000026", "03050205", "品名138", "case", 10, 1], ["SO2606000051", "03400205", "品名150", "case", 1, 1], ["SO2606000056", "03400205", "品名150", "case", 1, 1], ["SO2606000094", "04200105", "品名151", "case", 300, 1], ["SO2509000002", "03040105", "品名185", "case", 3512, 1], ["SO2606000068", "02600205", "品名193", "case", 200, 1], ["SO2606000042", "02600205", "品名193", "case", 1, 1], ["SO2606000045", "02600205", "品名193", "case", 1, 1], ["SO2606000085", "02600205", "品名193", "case", 200, 1], ["SO2606000054", "02600205", "品名193", "case", 1, 1], ["SO2606000060", "02600205", "品名193", "case", 50, 1], ["SO2606000059", "02600205", "品名193", "case", 45, 1], ["SO2606000046", "02610105", "品名194", "case", 1, 1], ["SO2606000047", "02610205", "品名199", "case", 1, 1], ["SO2606000048", "02610205", "品名199", "case", 1, 1], ["SO2606000028", "02630105", "品名206", "case", 2, 0.5], ["SO2606000009", "02630105", "品名206", "case", 3, 1], ["SO2606000020", "02630105", "品名206", "case", 6, 0.5], ["SO2606000012", "02630105", "品名206", "case", 6, 0.5], ["SO2606000058", "02630105", "品名206", "case", 112, 1], ["SO2606000016", "02630105", "品名206", "case", 3, 0.5], ["SO2606000018", "02630105", "品名206", "case", 4, 0.5], ["SO2606000013", "02630105", "品名206", "case", 1, 0.5], ["SO2606000015", "02630105", "品名206", "case", 2, 0.5], ["SO2606000007", "02630105", "品名206", "case", 1, 1], ["SO2606000017", "02630105", "品名206", "case", 4, 0.5], ["SO2606000093", "03010105", "品名218", "case", 800, 1], ["SO2606000050", "03010105", "品名218", "case", 1, 1], ["SO2606000055", "03010105", "品名218", "case", 1, 1], ["SO2606000092", "03010105", "品名218", "case", 400, 1], ["SO2606000057", "03010105", "品名218", "case", 3, 1], ["SO2606000015", "01300103", "品名242", "case", 2, 0.5], ["SO2606000016", "01300103", "品名242", "case", 3, 0.5], ["SO2606000018", "01300103", "品名242", "case", 4, 0.5], ["SO2601000002", "01300103", "品名242", "case", 100, 0.909091], ["SO2606000008", "01300103", "品名242", "case", 2, 1], ["SO2606000012", "01300103", "品名242", "case", 6, 0.5], ["SO2606000010", "01300103", "品名242", "case", 4, 1], ["SO2601000001", "01300103", "品名242", "case", 100, 0.909091], ["SO2606000023", "01300103", "品名242", "case", 4, 1], ["SO2606000083", "01300103", "品名242", "case", 100, 0.909091], ["SO2606000019", "01300103", "品名242", "case", 6, 1], ["SO2606000025", "01300103", "品名242", "case", 10, 1], ["SO2509000001", "01300103", "品名242", "case", 3562, 1], ["SO2606000017", "01300103", "品名242", "case", 4, 0.5], ["SO2606000011", "01300103", "品名242", "case", 4, 1], ["SO2606000053", "01300103", "品名242", "case", 1, 1], ["SO2606000013", "01300103", "品名242", "case", 1, 0.5], ["SO2606000028", "01300103", "品名242", "case", 2, 0.5], ["SO2606000091", "01300103", "品名242", "case", 100, 0.909091], ["SO2606000020", "01300103", "品名242", "case", 6, 0.5], ["SO2606000032", "01300203", "品名243", "case", 4, 0.5], ["SO2606000035", "01300203", "品名243", "case", 5, 0.5], ["SO2606000034", "01300203", "品名243", "case", 3, 0.5], ["SO2601000001", "02600105", "品名244", "case", 10, 0.090909], ["SO2606000081", "02600105", "品名244", "case", 1, 1], ["SO2606000076", "02600105", "品名244", "case", 1, 1], ["SO2606000082", "02600105", "品名244", "case", 1, 1], ["SO2606000084", "02600105", "品名244", "case", 1, 1], ["SO2606000091", "02600105", "品名244", "case", 10, 0.090909], ["SO2606000088", "02600105", "品名244", "case", 1, 1], ["SO2606000089", "02600105", "品名244", "case", 1, 1], ["SO2606000090", "02600105", "品名244", "case", 1, 1], ["SO2606000035", "02600105", "品名244", "case", 5, 0.5], ["SO2601000002", "02600105", "品名244", "case", 10, 0.090909], ["SO2606000034", "02600105", "品名244", "case", 3, 0.5], ["SO2606000074", "02600105", "品名244", "case", 1, 1], ["SO2606000086", "02600105", "品名244", "case", 1, 1], ["SO2606000071", "02600105", "品名244", "case", 100, 1], ["SO2606000032", "02600105", "品名244", "case", 4, 0.5], ["SO2606000083", "02600105", "品名244", "case", 10, 0.090909], ["SO2606000087", "02600105", "品名244", "case", 1, 1], ["SO2606000077", "02600105", "品名244", "case", 10, 1], ["SO2606000027", "E00121H", "品名558", "ko", 1, 1], ["SO2606000029", "B00031M", "品名581", "ko", 2, 0.5], ["SO2606000029", "B00042M", "品名586", "ko", 2, 0.5], ["SO2606000024", "00005", "品名829", "g", 10, 1]]
  sessionHandler.getActiveSession().pushMessage(msgItem)

  msgItem = MessageFactory.createAiMsg()
  msgItem.msgType = MessageType.PIE
  msgItem.title = "受注における各品目の取引数量割合"
  msgItem.chartData = {"品名12": 1, "品名23": 1, "品名101": 1, "品名138": 1, "品名150": 1, "品名151": 1, "品名185": 1, "品名193": 1, "品名194": 1, "品名199": 1, "品名206": 0.5, "品名218": 1, "品名242": 0.5, "品名243": 0.5, "品名244": 1, "品名558": 1, "品名581": 0.5, "品名586": 0.5, "品名829": 1}
  sessionHandler.getActiveSession().pushMessage(msgItem)

  msgItem = MessageFactory.createAiMsg()
  msgItem.msgType = MessageType.TEXT
  msgItem.content = "1. 主要発見:\n" +
      "- データ上の「受注内取引数量割合」は4種類に集中している: 1（48件、60.0%）、0.5（24件、30.0%）、0.909091（4件、5.0%）、0.090909（4件、5.0%）。\n" +
      "- 比率1はその受注内で該当品目が取引数量の100%を占める行（単一品目受注または当該行が受注量を占有）。\n" +
      "- 比率0.5は同受注内で該当品目が約50%を占める行（受注内で数量が分割されている）。\n" +
      "- 比率0.909091 / 0.090909 は受注内で極端に数量が偏っている行（約90.9%と9.09%の組み合わせが存在）。\n" +
      "\n" +
      "2. 業務リスク:\n" +
      "- 比率1が多い（60%）: 受注が単一行頼り、ピッキング/出荷が非効率化（SKU毎の個別処理増、大量返品・在庫偏在のリスク）。\n" +
      "- 比率0.5が多い（30%）: 同一受注で数量が分割される傾向は、ピッキングミスや部分出荷・伝票不一致の原因になりうる。\n" +
      "- 比率0.909091/0.090909 の存在: 極端に偏った数量配分は丸め誤差・単位換算の不整合、または誤入力の疑いがある。需給配分や請求計算で誤差が生じる可能性。\n" +
      "- 共通リスク: 受注登録・単位マスタ・集約ロジックに起因するデータ品質問題があると、在庫引当・出荷計画に影響する。\n" +
      "\n" +
      "3. 改善提案:\n" +
      "- 比率1対策:\n" +
      "  - 受注入力ルールを確認し、可能なら同一顧客・同一配送での受注統合を促す（バッチング・出荷指示の最適化）。\n" +
      "  - ピッキング指示を受注単位でなくトランザクション量に基づく集約に変更し手待ちを減らす。\n" +
      "- 比率0.5対策:\n" +
      "  - 受注行の分割要因（パッケージ単位、ロット、別倉庫等）を調査し、分割が不要なら入力ルールで防止。\n" +
      "  - ピッキングリストと出荷検品プロセスに受注内複数行の整合チェックを追加。\n" +
      "- 0.909091 / 0.090909 対策:\n" +
      "  - 該当受注での数量比の発生原因を抽出（単位換算、丸め、手入力）し原因に応じてマスタ/入力ロジックを修正。\n" +
      "  - 数量合計と行別比率のバリデーション（一定閾値越え時のアラート）を導入。\n" +
      "- 共通改善:\n" +
      "  - 受注サンプルを抽出してデータ品質監査を実施し、頻出パターンを元に入力テンプレートやバリデーションを強化する。"

  sessionHandler.getActiveSession().pushMessage(msgItem)

}

function pushDemoSession04() {

  sessionHandler.createSession("demo04", "[サンプル] 日別受注数量の推移\n")

  let msgItem = MessageFactory.createUserMsg()
  msgItem.content = "日別受注数量の推移を分析してください"
  sessionHandler.getActiveSession().pushMessage(msgItem)

  msgItem = MessageFactory.createAiMsg()
  msgItem.msgType = MessageType.TABLE
  msgItem.field = ["日付", "受注数量"]
  msgItem.chartData = [
    ["2026-05-01", 120],
    ["2026-05-02", 135],
    ["2026-05-03", 150],
    ["2026-05-04", 160],
    ["2026-05-05", 155],
    ["2026-05-06", 170],
    ["2026-05-07", 190],
    ["2026-05-08", 210],
    ["2026-05-09", 205],
    ["2026-05-10", 230]
  ]
  sessionHandler.getActiveSession().pushMessage(msgItem)

  msgItem = MessageFactory.createAiMsg()
  msgItem.msgType = MessageType.LINE
  msgItem.title = "日別受注数量推移"
  msgItem.chartData = {
    "05-01": 120,
    "05-02": 135,
    "05-03": 150,
    "05-04": 160,
    "05-05": 155,
    "05-06": 170,
    "05-07": 190,
    "05-08": 210,
    "05-09": 205,
    "05-10": 230
  }
  sessionHandler.getActiveSession().pushMessage(msgItem)

  msgItem = MessageFactory.createAiMsg()
  msgItem.msgType = MessageType.TEXT
  msgItem.content =
      "1. 主要発見:\n" +
      "- 受注数量は全体として増加傾向にあり、05-01の120件から05-10には230件まで増加。\n" +
      "- 05-05と05-09で一時的な減少が見られるが、全体トレンドは右肩上がり。\n" +
      "- 05-07以降は200件前後で推移し、需要が拡大している可能性。\n" +
      "\n" +
      "2. 業務リスク:\n" +
      "- 受注増加に伴い、出荷処理・倉庫ピッキングの負荷が高まる可能性。\n" +
      "- 急激な受注増加が続くと、在庫不足や配送遅延が発生するリスク。\n" +
      "\n" +
      "3. 改善提案:\n" +
      "- 受注増加が継続する場合、出荷人員やピッキングラインの増強を検討。\n" +
      "- 需要予測モデルを導入し、事前に在庫補充計画を立てる。\n" +
      "- 日別データだけでなく週次・月次分析も行い、季節性やキャンペーン影響を確認。"


  sessionHandler.getActiveSession().pushMessage(msgItem)

}

</script>

<style scoped>
:root {
  --ai-blue: #0066FF;
  --ai-blue-soft: #EBF5FF;
  --ai-blue-deep: #004FCC;

  --ai-text-main: #1E293B;
  --ai-text-muted: #64748B;

  --ai-border: #E2E8F0;
  --ai-bg-panel: #FFFFFF;

  --ai-shadow-soft: 0 6px 20px rgba(0, 0, 0, 0.06);
  --ai-shadow-blue: 0 8px 24px rgba(0, 102, 255, 0.25);
}

.app-container {
  display: flex;
  width: 100vw;
  height: 100vh;
  background-color: #F8FAFC;
  overflow: hidden;
  color: #1E293B;
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Noto Sans JP", sans-serif;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: radial-gradient(circle at 50% 0%, #FFFFFF 0%, #F1F5F9 100%);
  overflow: hidden;
}

.chat-viewport {
  flex: 1;
  overflow-y: auto;
  padding: 40px 0;
}

.message-list {
  max-width: 1150px;
  margin: 0 auto;
  padding: 0 40px;
}

.message-row {
  display: flex;
  gap: 20px;
  margin-bottom: 45px;
}

:deep(.highlight) {
  color: #0055FF;
  background: #EBF5FF;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
}

:deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  font-size: 13px;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  overflow: hidden;
}

:deep(th) {
  background: #F1F5F9;
  color: #475569;
  font-weight: 600;
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #E2E8F0;
}

:deep(td) {
  padding: 12px;
  border-bottom: 1px solid #E2E8F0;
}

:deep(tr:hover td) {
  background: #F8FBFF;
}

.chart-container-box {
  margin-top: 18px;
  padding: 16px;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, .04);
}

.input-area {
  padding: 16px 40px;
}

.input-wrapper {
  max-width: 1020px;
  margin: 0 auto;
  background: #FFFFFF;
  border: 1px solid #D6E4FF;
  border-radius: 16px;
  padding: 12px 18px;
  display: flex;
  align-items: center;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
}

textarea {
  flex: 1;
  border: none;
  background: transparent;
  resize: none;
  outline: none;
  font-size: 15px;
  line-height: 1.6;
  max-height: 200px;   /* 防止无限变高 */
  overflow-y: auto;    /* 超出后内部滚动 */
}

.send-btn {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid var(--ai-border);
  background: linear-gradient(
      180deg,
      #F8FBFF 0%,
      #EAF2FF 100%
  );
  box-shadow: var(--ai-shadow-soft);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s ease;
  color: var(--ai-text-muted);
}

.send-icon {
  width: 20px;
  height: 20px;
  stroke-width: 1.8;
}

.send-btn:hover:not(:disabled) {
  transform: translateY(-1px) scale(1.04);
  background: linear-gradient(
      180deg,
      #EEF5FF 0%,
      #DCE9FF 100%
  );
  box-shadow: 0 8px 24px rgba(0, 102, 255, 0.18);
  color: var(--ai-blue);
}

.send-btn.active {
  border: none;
  background: linear-gradient(
      180deg,
      #2F7BFF 0%,
      #0055E6 100%
  );
  color: #FFFFFF;
  box-shadow: var(--ai-shadow-blue);
}

.send-btn:active:not(:disabled) {
  transform: scale(0.94);
  box-shadow: 0 4px 14px rgba(0, 102, 255, 0.25);
}

.send-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  box-shadow: none;
}

.send-btn.stop {
  background: #E5E7EB;
  border: 1px solid #D1D5DB;

  border-radius: 50%;

  display: flex;
  align-items: center;
  justify-content: center;

  transition: all 0.2s ease;
}

.send-btn.stop:hover {
  background: #D1D5DB;
}

.stop-square {
  width: 12px;
  height: 12px;

  background: #ef4444;

  border-radius: 3px;

  animation: stopBreath 1.5s ease-in-out infinite;
}

@keyframes stopBreath {

  0% {
    transform: scale(0.9);
    opacity: 0.7;
  }

  50% {
    transform: scale(1.15);
    opacity: 1;
  }

  100% {
    transform: scale(0.9);
    opacity: 0.7;
  }

}

.mode-toggle {
  height: 36px;
  padding: 0 14px;
  margin-right: 12px;
  border-radius: 10px;
  border: 1px solid var(--ai-border);
  background: #F8FAFC;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.mode-toggle:hover {
  background: #EEF5FF;
}

/* ⚠️ 必须和 key 一模一样 */
.mode-toggle.search {
  background: #EBF5FF;
  color: #0066FF;
  border-color: #BFD7FF;
}

.mode-toggle.predict {
  background: #F8FAFC;
  color: #1E293B;
}

::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-thumb {
  background: #CBD5E1;
  border-radius: 6px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94A3B8;
}
</style>